/* ─────────────────────────────────────────────────────────────
   OVERFLOW TAB BAR

   The stock-detail bar carries 17 tabs — far more than fits on most screens,
   and a horizontally-scrolling strip hid most of them behind an invisible
   affordance. This renders as many tabs as actually fit the container and
   collapses the remainder into a "More ▾" menu.

   How it measures: a hidden, aria-hidden copy of every label (plus the "More"
   button) is rendered off-flow so each natural width is known even while the
   tab is collapsed. A ResizeObserver tracks the real container width, so the
   split recomputes on window resize, sidebar toggle or font change — no
   hardcoded breakpoints.

   The active tab is ALWAYS visible: if it falls into the overflow it is pulled
   into the last visible slot and the tab it displaces moves into the menu. That
   keeps the sliding indicator meaningful and stops the current page from
   appearing unselected.
───────────────────────────────────────────────────────────── */

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Menu, MenuItem } from "@mui/material";
import { ChevronDown } from "lucide-react";

const GAP = 2;          // matches the container's inter-button gap
const H_PADDING = 8;    // container padding (var(--spacing-xs) each side)

const tabButtonStyle = (isActive) => ({
  padding: "6px 10px",
  fontSize: "var(--font-size-sm)",
  fontWeight: "var(--font-weight-semibold)",
  borderRadius: "var(--radius-lg)",
  color: isActive ? "var(--color-text-primary)" : "var(--color-text-muted)",
  transition: "var(--transition-fast)",
  background: "none",
  border: "none",
  cursor: "pointer",
  whiteSpace: "nowrap",
});

const OverflowTabs = ({ tabs, activeLabel, onSelect }) => {
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const buttonRefs = useRef({});

  const [widths, setWidths] = useState([]);
  const [moreWidth, setMoreWidth] = useState(0);
  const [available, setAvailable] = useState(0);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });

  /* ── Measure natural label widths from the hidden copy ── */
  useLayoutEffect(() => {
    const node = measureRef.current;
    if (!node) return;
    const kids = Array.from(node.children);
    // last child is the "More" button template
    setWidths(kids.slice(0, tabs.length).map((el) => el.getBoundingClientRect().width));
    const moreEl = kids[tabs.length];
    if (moreEl) setMoreWidth(moreEl.getBoundingClientRect().width);
  }, [tabs]);

  /* ── Track the real container width ── */
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const update = () => setAvailable(node.clientWidth - H_PADDING);
    update();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    }
    const ro = new ResizeObserver(update);
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  /* ── How many tabs fit, and which ones ── */
  const { visibleIdx, overflowIdx } = useMemo(() => {
    const all = tabs.map((_, i) => i);
    if (!widths.length || !available) return { visibleIdx: all, overflowIdx: [] };

    const fit = (limit) => {
      let n = 0;
      let used = 0;
      for (let i = 0; i < widths.length; i++) {
        const w = widths[i] + (i > 0 ? GAP : 0);
        if (used + w > limit) break;
        used += w;
        n++;
      }
      return n;
    };

    let n = fit(available);
    // If anything overflows we must also make room for the "More" button.
    if (n < tabs.length) n = fit(available - moreWidth - GAP);
    n = Math.max(1, Math.min(n, tabs.length));

    if (n >= tabs.length) return { visibleIdx: all, overflowIdx: [] };

    const visible = all.slice(0, n);
    const activeIndex = tabs.indexOf(activeLabel);
    // Pull the active tab in if it would otherwise be hidden.
    if (activeIndex >= n) visible[n - 1] = activeIndex;

    const visibleSet = new Set(visible);
    return { visibleIdx: visible, overflowIdx: all.filter((i) => !visibleSet.has(i)) };
  }, [tabs, widths, available, moreWidth, activeLabel]);

  /* ── Slide the indicator under the active tab ── */
  useEffect(() => {
    const el = buttonRefs.current[activeLabel];
    if (el) setIndicator({ left: el.offsetLeft, width: el.offsetWidth });
    else setIndicator({ left: 0, width: 0 });
  }, [activeLabel, visibleIdx]);

  const closeMenu = useCallback(() => setMenuAnchor(null), []);

  const pick = useCallback(
    (label) => {
      closeMenu();
      onSelect(label);
    },
    [closeMenu, onSelect]
  );

  const hasOverflow = overflowIdx.length > 0;

  return (
    <>
      {/* ── Hidden measurer: natural widths for every label + the More button ── */}
      <div
        ref={measureRef}
        aria-hidden="true"
        style={{
          position: "absolute",
          top: -9999,
          left: -9999,
          height: 0,
          overflow: "hidden",
          display: "flex",
          pointerEvents: "none",
          visibility: "hidden",
        }}
      >
        {tabs.map((tab) => (
          <span key={tab} style={tabButtonStyle(false)}>
            {tab}
          </span>
        ))}
        <span style={{ ...tabButtonStyle(false), display: "inline-flex", alignItems: "center", gap: 4 }}>
          More
          <ChevronDown size={14} />
        </span>
      </div>

      <div
        ref={containerRef}
        className="relative flex items-center"
        style={{
          backgroundColor: "var(--color-surface)",
          border: "1px solid var(--color-border-primary)",
          borderRadius: "var(--radius-xl)",
          padding: "var(--spacing-xs)",
          gap: `${GAP}px`,
          overflow: "hidden",
        }}
      >
        <div
          className="absolute top-1.5 h-[calc(100%-12px)] transition-all duration-300 ease-out"
          style={{
            backgroundColor: "var(--color-surface-hover)",
            border: "1px solid var(--color-border-secondary)",
            borderRadius: "var(--radius-lg)",
            left: indicator.left,
            width: indicator.width,
            opacity: indicator.width ? 1 : 0,
          }}
        />

        {visibleIdx.map((i) => {
          const tab = tabs[i];
          const isActive = activeLabel === tab;
          return (
            <button
              key={tab}
              ref={(el) => {
                buttonRefs.current[tab] = el;
              }}
              onClick={() => onSelect(tab)}
              aria-current={isActive ? "page" : undefined}
              className="relative z-10"
              style={tabButtonStyle(isActive)}
              onMouseEnter={(e) => {
                if (!isActive) e.currentTarget.style.color = "var(--color-text-secondary)";
              }}
              onMouseLeave={(e) => {
                if (!isActive) e.currentTarget.style.color = "var(--color-text-muted)";
              }}
            >
              {tab}
            </button>
          );
        })}

        {hasOverflow && (
          <button
            type="button"
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            aria-haspopup="menu"
            aria-expanded={Boolean(menuAnchor)}
            aria-label={`More tabs (${overflowIdx.length})`}
            className="relative z-10"
            style={{
              ...tabButtonStyle(false),
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              marginLeft: "auto",
              flexShrink: 0,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--color-text-secondary)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "var(--color-text-muted)")}
          >
            More
            <ChevronDown
              size={14}
              style={{
                transform: menuAnchor ? "rotate(180deg)" : "rotate(0deg)",
                transition: "var(--transition-fast)",
              }}
            />
          </button>
        )}
      </div>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              minWidth: 210,
              maxHeight: 420,
              backgroundColor: "var(--color-surface)",
              border: "1px solid var(--color-border-primary)",
              borderRadius: "var(--radius-lg)",
              boxShadow: "var(--shadow-lg)",
            },
          },
        }}
      >
        {overflowIdx.map((i) => {
          const tab = tabs[i];
          const isActive = activeLabel === tab;
          return (
            <MenuItem
              key={tab}
              onClick={() => pick(tab)}
              selected={isActive}
              sx={{
                fontSize: "var(--font-size-sm)",
                fontWeight: "var(--font-weight-semibold)",
                color: isActive ? "var(--color-primary)" : "var(--color-text-secondary)",
                "&:hover": { backgroundColor: "var(--color-surface-hover)" },
                "&.Mui-selected": { backgroundColor: "var(--color-surface-hover)" },
              }}
            >
              {tab}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default OverflowTabs;
